"use client";

import React, { useEffect, useRef, useState } from "react";
import { ClipboardList, Activity, Sparkles, type LucideIcon } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  title: "Danışmanlık ve Planlama",
  description: "Uzman hekimlerimizle ilk görüşme, detaylı muayene ve size özel tedavi planının oluşturulması. Beklentilerinizi dinliyor, en uygun çözümleri sunuyoruz.",
  icon: ClipboardList
}, {
  title: "Tedavi Aşaması",
  description: "Modern teknoloji ve konforlu klinik ortamımızda, planlanan tedavinin titizlikle uygulanması. Her adımda şeffaf iletişim ve yüksek standartlarda hizmet.",
  icon: Activity
}, {
  title: "Sonuç ve Takip",
  description: "Yeni gülüşünüze kavuşmanız ve uzun vadeli ağız sağlığınız için düzenli kontrollerin yapılması. Tedavi sonrası süreçte de yanınızdayız.",
  icon: Sparkles
}];
interface TimelineStepProps {
  step: {
    title: string;
    description: string;
    icon: LucideIcon;
  };
  index: number;
  isLast: boolean;
}
function TimelineStep({
  step,
  index,
  isLast
}: TimelineStepProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <div ref={ref} data-index={index} className={`flex gap-8 transition-all duration-[800ms] ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-[20px] opacity-0"}`}>
      <div className="flex flex-col items-center">
        <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-background shadow-sm border border-border">
          <step.icon className="size-6 text-primary" strokeWidth={1} />
        </div>
        {!isLast && <div className="my-4 h-[80px] w-[1px] bg-primary/40" />}
      </div>
      
      <div className={`pt-2 ${!isLast ? "pb-4" : ""}`}>
        <h3 className="mb-3 font-default text-2xl font-medium text-foreground">
          {step.title}
        </h3>
        <p className="font-sans text-muted-foreground leading-relaxed max-w-xl">
          {step.description}
        </p>
      </div>
    </div>;
}
export default function TreatmentProcess() {
  return <section id="tedavi-süreci" className="bg-secondary py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <h2 className="mb-20 text-center font-default text-[40px] md:text-[48px] leading-tight text-foreground">
          Tedavi Süreci
        </h2>
        
        <div className="mx-auto max-w-2xl flex flex-col">
          {steps.map((step, i) => <TimelineStep key={i} step={step} index={i} isLast={i === steps.length - 1} />)}
        </div>
      </div>
    </section>;
}